/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops UI のデータ取得フック。一覧（offset ページング）とステート別件数は実 API（/ops/*）に接続済み。detail / 依頼者ビュー / 担当割当は後続スライスで実 API 化予定のため現状モック fixture を返す。UI は本フック経由でのみデータを読む（直接 fetch しない）
 * @graph-connects none
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  OpsRequestDraft,
  OpsSendBackReason,
  OpsSloGranularity,
  OpsSloTrendMonths,
  OpsStateHistoryEvent,
} from '@cortex/ops-core';
import { OPS_REQUESTS } from '../lib/ops-fixtures.js';
import {
  opsApi,
  type OpsAssignmentEdit,
  type OpsAssignmentEvent,
  type OpsContentEdit,
  type OpsCreateRequestExtras,
  type OpsRelatedTicketInput,
  type OpsRequestsPage,
  type OpsServiceFilter,
  type OpsSimilarResult,
  type OpsStatusCounts,
  type OpsStatusFilter,
} from '../lib/api/ops.js';
import type {
  CreateOpsBugTicketInput,
  CreatedOpsBugTicket,
  OpsAssignment,
  OpsBugTicketDraft,
  OpsRequest,
  OpsService,
  OpsState,
  OpsUrgency,
} from '../types/ops.js';

// 絞り込み・ページ・件数の型は API クライアントを単一 source とし、既存 import 経路維持のため再エクスポートする。
export type {
  OpsRequestsPage,
  OpsServiceFilter,
  OpsSimilarResult,
  OpsStatusCounts,
  OpsStatusFilter,
};

/** 1 ページの件数（実 API の limit）。 @graph-connects none */
export const OPS_PAGE_SIZE = 15;

/** 一覧ページ取得の追加オプション（依頼者 / 対応者の絞り込み、フェッチ有効化）。 @graph-connects none */
export interface OpsRequestsPageOptions {
  /** 依頼者 nickName で絞り込む（My-Requests の「起票」レンズ）。 */
  requester?: string;
  /** 対応者 nickName で絞り込む（My-Requests の「担当」レンズ）。 */
  assignee?: string;
  /** false のあいだフェッチしない（nickName 未解決時など）。既定 true。 */
  enabled?: boolean;
  /** フリーワード検索。空白区切りの全トークンを依頼内容に含む依頼に絞る。未指定で絞らない。 */
  q?: string;
}

/**
 * ops 依頼をページ単位で取得する（fetch-more-on-scroll）。`GET /ops/requests?status&service&offset&limit&q`。
 * requester / assignee で依頼者・対応者の絞り込みもできる（My-Requests 用）。q はフリーワード / 番号検索。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ステータス / サービス / 依頼者 / 対応者 / フリーワード（番号含む）で絞り込んだ依頼を offset ページングで遅延取得する。検索語は query key に含むため、変えると先頭ページから取り直す
 * @graph-connects opsApi [calls] /ops/requests を叩いて 1 ページ取得
 */
export function useOpsRequestsPage(
  status: OpsStatusFilter,
  service: OpsServiceFilter,
  options: OpsRequestsPageOptions = {},
) {
  const { requester, assignee, enabled = true, q } = options;
  return useInfiniteQuery({
    // 検索語も key に含める（変えたら新しい結果セットとして先頭ページから取り直す）。
    queryKey: [
      'ops',
      'requests',
      'page',
      status,
      service,
      requester ?? null,
      assignee ?? null,
      q ?? null,
    ],
    initialPageParam: 0,
    queryFn: ({ pageParam }): Promise<OpsRequestsPage> =>
      opsApi.listRequests({
        status,
        service,
        offset: pageParam,
        limit: OPS_PAGE_SIZE,
        requester,
        assignee,
        q,
      }),
    getNextPageParam: (lastPage): number | null => lastPage.nextOffset,
    enabled,
  });
}

/**
 * 新規依頼フォームの重複スキャン用に、対象ユーザー（顧客）の既存 ops 依頼を取得する。複数 User ID に
 * 対応し、`GET /ops/requests?userId=<カンマ区切り>` で **いずれかを対象に含む**依頼を横断取得する（match-any）。
 * enabled が false / ID が空のあいだはフェッチしない。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 新規依頼フォームで対象ユーザー ID（複数可）の既存依頼（重複候補）を取得する。オープンな依頼があれば重複警告に使う
 * @graph-connects opsApi [calls] /ops/requests?userId（カンマ区切り match-any）を叩いて対象ユーザーの依頼を取得
 */
export function useOpsRelatedRequests(
  userIds: string[],
  enabled: boolean,
): UseQueryResult<OpsRequestsPage> {
  const joined = userIds.join(',');
  return useQuery({
    queryKey: ['ops', 'related', joined],
    queryFn: (): Promise<OpsRequestsPage> =>
      opsApi.listRequests({ status: 'all', service: 'all', offset: 0, limit: 8, userId: joined }),
    enabled: enabled && userIds.length > 0,
  });
}

/**
 * 新規依頼フォームの重複スキャン用に、内容が類似する ops 依頼 + 既存 Backlog バグチケットを取得する。
 * `GET /ops/similar?text`。enabled が false / text 空のあいだはフェッチしない。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼本文から内容が類似する ops 依頼と既存バグチケット（新規起票前の重複候補）を取得する
 * @graph-connects opsApi [calls] /ops/similar を叩いて類似 ops + バグチケット取得
 */
export function useOpsSimilar(text: string, enabled: boolean): UseQueryResult<OpsSimilarResult> {
  return useQuery({
    queryKey: ['ops', 'similar', text],
    queryFn: (): Promise<OpsSimilarResult> => opsApi.getSimilar(text),
    enabled: enabled && text.trim() !== '',
  });
}

/**
 * 新規 ops 依頼を作成する mutation。`POST /ops/requests`。成功時に作成された OpsRequest を返す。
 * requester はサーバーが認証セッションから付与する（クライアントは送らない）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 新規 ops 依頼を作成する（フォーム送信）。成功で作成された依頼（採番済み id 付き）を返す
 * @graph-connects opsApi [calls] /ops/requests に POST して新規依頼を作成
 */
export function useCreateOpsRequest(): UseMutationResult<
  OpsRequest,
  Error,
  { draft: OpsRequestDraft; extras?: OpsCreateRequestExtras }
> {
  return useMutation({
    mutationFn: ({ draft, extras }): Promise<OpsRequest> => opsApi.createRequest(draft, extras),
  });
}

/**
 * ステート別件数（タブのバッジ用）。`GET /ops/requests/status-counts?service&q`。検索中は一覧と同じ
 * 条件で数えるため、検索語もそのまま渡す（バッジだけ全件のままだと一覧と数が食い違う）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business サービス絞り込み + フリーワード検索の条件下でのステート別件数を取得する
 * @graph-connects opsApi [calls] /ops/requests/status-counts を叩いて件数取得
 */
export function useOpsStatusCounts(
  service: OpsServiceFilter,
  q?: string,
): UseQueryResult<OpsStatusCounts> {
  return useQuery({
    queryKey: ['ops', 'counts', service, q ?? null],
    queryFn: (): Promise<OpsStatusCounts> => opsApi.statusCounts(service, q),
  });
}

/**
 * SLO ダッシュボード用に、指定期間（当月 / 当四半期、または過去の月 / 四半期）の依頼を全件取得する。
 * `GET /ops/slo?period&start`。SLA / SLO / バグ転換の集計は呼び出し側（ダッシュボード）が ops-core で行う。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 期間（当月 / 当四半期、または指定した過去の月 / 四半期）内の ops 依頼を取得する（SLO ダッシュボードの集計元）
 * @graph-connects opsApi [calls] /ops/slo を叩いて期間内依頼を取得
 */
export function useOpsSlo(
  period: OpsSloGranularity,
  start?: string,
): UseQueryResult<{ requests: OpsRequest[] }> {
  return useQuery({
    queryKey: ['ops', 'slo', period, start ?? null],
    queryFn: (): Promise<{ requests: OpsRequest[] }> => opsApi.getSlo(period, start),
  });
}

/**
 * 内訳タブ（サービス別の差戻し）用に、useOpsSlo と同じ期間の native 依頼のステート履歴を取得する。
 * `GET /ops/slo/state-history?period&start`。内訳タブを開いたときだけ取得する（enabled）。差戻しの集計は
 * 呼び出し側が ops-core で行う。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business SLO と同じ期間に起票された native ops 依頼のステート履歴を取得する（内訳タブのサービス別差戻し統計の集計元）。内訳タブ表示中だけ取得する
 * @graph-connects opsApi [calls] /ops/slo/state-history を叩いて期間内のステート履歴を取得
 */
export function useOpsSloStateHistory(
  period: OpsSloGranularity,
  start: string | undefined,
  enabled: boolean,
): UseQueryResult<{ events: OpsStateHistoryEvent[] }> {
  return useQuery({
    queryKey: ['ops', 'slo', 'state-history', period, start ?? null],
    queryFn: (): Promise<{ events: OpsStateHistoryEvent[] }> =>
      opsApi.getSloStateHistory(period, start),
    enabled,
  });
}

/**
 * SLO ボリューム推移チャート用に、直近 N ヶ月（6 / 12）に起票された依頼を全件取得する。
 * `GET /ops/slo/trend?months`。月別の件数 / 工数 / SLO 達成率の集計は呼び出し側が ops-core で行う。
 * queryKey は months ごとに独立なので、6 ↔ 12 切替時はその月数ぶんを丸ごと取得する（差分取得ではない）。
 * 一度取得した月数はキャッシュされ、再選択時は再フェッチしない。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 直近 N ヶ月（6 / 12）の ops 依頼を取得する（SLO ボリューム推移チャートの集計元）
 * @graph-connects opsApi [calls] /ops/slo/trend を叩いて直近 N ヶ月の依頼を取得
 */
export function useOpsSloTrend(
  months: OpsSloTrendMonths,
): UseQueryResult<{ requests: OpsRequest[] }> {
  return useQuery({
    queryKey: ['ops', 'slo', 'trend', months],
    queryFn: (): Promise<{ requests: OpsRequest[] }> => opsApi.getSloTrend(months),
  });
}

/**
 * ops 依頼を 1 件取得する。`GET /ops/requests/{id}`（404 は null）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business id（slug:No）で単一依頼を取得する（見つからなければ null）。enabled=false なら取得しない（補足対応の元依頼のように id が無いことがある呼び出し用）
 * @graph-connects opsApi [calls] /ops/requests/{id} を叩いて単一依頼取得
 */
export function useOpsRequest(id: string, enabled = true): UseQueryResult<OpsRequest | null> {
  return useQuery({
    queryKey: ['ops', 'requests', id],
    queryFn: (): Promise<OpsRequest | null> => opsApi.getRequest(id),
    enabled,
  });
}

/**
 * 依頼詳細キャッシュ（['ops','requests', id]）を mutation のレスポンスで即時に差し替えつつ、直前に
 * キャッシュ済みの notes タイムラインを保持し、**そのうえで詳細クエリを invalidate する**。
 *
 * setQueryData だけでは活動ログが遅れる: サーバーは更新後 DTO を組み立ててから活動ログを追記するため、
 * mutation のレスポンスには **その操作自身のエントリがまだ入っていない**。詳細クエリは staleTime 5 分 +
 * refetchOnWindowFocus 無効なので、invalidate しないと「ステータスを変えたのに Activity に出ない」状態が
 * 数分続く。setQueryData で即時反映（ちらつき防止）し、invalidate で追記後の正を取り直す。
 *
 * メモ / 工数 / 関連チケット / 添付のように活動ログを積まない mutation もここを通るため、それらでは
 * 再取得が 1 回ぶん無駄になる。呼び出し側ごとに要否を分岐させるより「詳細を変えたら取り直す」1 本の
 * 規則にしておくほうが、活動ログを積む操作が増えたときに付け忘れない（ops の更新頻度では誤差）。
 *
 * notes の保持が要るのは、assignee / state / urgency / escalate / de-escalate / memo の各 mutation が
 * バックエンドから `notes: []` を返すため（それらの経路は notes を読まない）。素直に差し替えると再取得が
 * 返るまでの一瞬 notes タイムラインが消える。notes 自体を更新する useAddOpsNote だけはサーバー返却の
 * notes をそのまま採用する（この関数を通さない）。
 * @graph-connects none
 */
function setDetailPreservingNotes(queryClient: QueryClient, id: string, updated: OpsRequest): void {
  queryClient.setQueryData<OpsRequest | null>(['ops', 'requests', id], (prev) =>
    prev ? { ...updated, notes: prev.notes } : updated,
  );
  void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', id] });
}

/**
 * 単一 ops 依頼の担当者を変更する mutation（PATCH /ops/requests/{id}/assignee）。
 * 成功時に該当依頼の詳細キャッシュを更新し、一覧ページを invalidate する。null / 空文字で未割当に戻す。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の割当ドロップダウン / 着手ボタンから担当者を変更する。成功で詳細キャッシュを差し替え一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/assignee に PATCH して担当者を変更
 */
export function useSetOpsRequestAssignee(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; assigneeName: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assigneeName }): Promise<OpsRequest> =>
      opsApi.setRequestAssignee(id, assigneeName),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼の進捗ステートを変更する mutation（PATCH /ops/requests/{id}/state）。closed=解決済み。
 * 成功時に該当依頼の詳細キャッシュを差し替え、一覧ページとステート別件数（タブのバッジ）を invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細のステータス変更 / 解決ボタンから進捗ステートを変更する。成功で詳細キャッシュを差し替え、一覧とタブ件数を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/state に PATCH してステートを変更
 */
export function useSetOpsRequestState(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; state: OpsState; closeReport?: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state, closeReport }): Promise<OpsRequest> =>
      opsApi.setRequestState(id, state, closeReport),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
      // 件数バッジも再取得する。件数は現状 BQ ミラーのみを集計するため Firestore 発番の依頼の
      // ステート変更ではバッジは動かないが、Firestore∪BQ union（PR #3955）が入ると反映される。
      void queryClient.invalidateQueries({ queryKey: ['ops', 'counts'] });
    },
  });
}

/**
 * ops 依頼を依頼者へ差戻す mutation（POST /ops/requests/{id}/send-back）。差戻しモーダルで送信を確定した
 * ときに呼び、state を blocked にし起票スレッドへ message（人手承認済み）を返信する。成功で詳細キャッシュを
 * 更新し件数を再取得する（再開は既存の setRequestState('investigating') で行う）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 差戻しモーダルの送信で ops 依頼を依頼者へ差戻す（理由 + メッセージを記録、state=blocked + 起票スレッドへ差戻し理由 + メッセージ返信）。成功で詳細キャッシュを更新し件数を invalidate する
 * @graph-connects opsApi [calls] /ops/requests/{id}/send-back に POST して依頼者へ差戻す
 */
export function useSendBackOpsRequest(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; reason: OpsSendBackReason; message: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason, message }): Promise<OpsRequest> =>
      opsApi.sendBack(id, reason, message),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
      void queryClient.invalidateQueries({ queryKey: ['ops', 'counts'] });
    },
  });
}

/**
 * ops 依頼の対応結論の下書きを AI 生成する mutation（POST /ops/requests/{id}/close-report/draft）。
 * クローズ確認モーダルを開いたときに呼び、調査メモ（memo）と調査ログ（notes）から依頼者向けの結論を
 * 生成する。両方が空なら draft は空文字（モーダルが人手入力を促す）。生成のみで Slack へは投稿しない
 * （送信は解決時に承認後）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business クローズ確認モーダルで調査メモ（memo）と調査ログ（notes）から対応結論の下書きを AI 生成する。承認・編集は UI 側、送信は解決 PATCH で行う
 * @graph-connects opsApi [calls] /ops/requests/{id}/close-report/draft に POST して対応結論の下書きを生成
 */
export function useDraftOpsCloseReport(): UseMutationResult<
  { draft: string },
  Error,
  { id: string }
> {
  return useMutation({
    mutationFn: ({ id }): Promise<{ draft: string }> => opsApi.draftCloseReport(id),
  });
}

/**
 * 単一 ops 依頼の緊急度を変更する mutation（PATCH /ops/requests/{id}/urgency）。通常 / 当日 / 即時。
 * urgency は escalated とは独立した純粋な優先度で、この経路では escalated / CTO 通知は発火しない。
 * Slack 通知はしない。成功時に詳細キャッシュを差し替え、一覧ページとステート別件数も invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の緊急度ドロップダウンから依頼の緊急度を変更する（起票者-or-担当者）。成功で詳細キャッシュを差し替え、一覧とタブ件数を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/urgency に PATCH して緊急度を変更
 */
export function useSetOpsRequestUrgency(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; urgency: OpsUrgency }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, urgency }): Promise<OpsRequest> => opsApi.setRequestUrgency(id, urgency),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
      void queryClient.invalidateQueries({ queryKey: ['ops', 'counts'] });
    },
  });
}

/**
 * 単一 ops 依頼をエスカレーションする mutation（POST /ops/requests/{id}/escalate）。
 * escalated を立て #product_all へ通知し、利便性で urgency も即時へ引き上げる（サーバー側で担当エンジニアのみ
 * 許可）。成功時に該当依頼の詳細キャッシュを差し替え、一覧ページとステート別件数を invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の Escalate ボタンから依頼をエスカレーションする（escalated=true + urgency 即時）。成功で詳細キャッシュを差し替え、一覧とタブ件数を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/escalate に POST してエスカレーションする
 */
export function useEscalateOpsRequest(): UseMutationResult<OpsRequest, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }): Promise<OpsRequest> => opsApi.escalateRequest(id),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
      void queryClient.invalidateQueries({ queryKey: ['ops', 'counts'] });
    },
  });
}

/**
 * 単一 ops 依頼のエスカレーションを解除する mutation（POST /ops/requests/{id}/deescalate）。
 * escalated を false へ戻す undo（urgency には触れない。エスカレーション通知スレッドへ解除を返信する。サーバー側で
 * 担当エンジニアのみ許可）。成功時に該当依頼の詳細キャッシュを差し替え（ボタンが Escalate へトグルするため）、一覧
 * ページとステート別件数を invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の De-escalate ボタンから依頼のエスカレーションを解除する（escalated=false の undo。urgency は変更しない）。成功で詳細キャッシュを差し替え、一覧とタブ件数を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/deescalate に POST してエスカレーションを解除する
 */
export function useDeescalateOpsRequest(): UseMutationResult<OpsRequest, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }): Promise<OpsRequest> => opsApi.deescalateRequest(id),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
      void queryClient.invalidateQueries({ queryKey: ['ops', 'counts'] });
    },
  });
}

/**
 * 単一 ops 依頼の notes タイムラインに調査メモを 1 件追記する mutation（POST /ops/requests/{id}/notes）。
 * author はサーバーが認証セッションから付与する（クライアントは text だけ送る）。成功時に該当依頼の詳細キャッシュを
 * 差し替え（返却された request が新規メモを含む最新の notes を持つのでそのまま採用＝ notes は保持しない）、一覧
 * ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の notes タイムラインへ調査メモを追記する。成功で詳細キャッシュを最新の notes 付きに差し替え、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/notes に POST して調査メモを追記
 */
export function useAddOpsNote(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; text: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }): Promise<OpsRequest> => opsApi.addNote(id, text),
    onSuccess: (updated, { id }) => {
      // useAddOpsNote だけは notes を保持せず、サーバー返却の最新 notes（追記済み）をそのまま採用する。
      queryClient.setQueryData(['ops', 'requests', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼の調査メモ（memo スカラー）を編集する mutation（PATCH /ops/requests/{id}/memo）。
 * null / 空文字で消去。Firestore 発番の依頼のみ対象（legacy は 404）。成功時に該当依頼の詳細キャッシュを
 * 差し替えつつ直前の notes タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の調査メモ（1 依頼 1 文字列）を編集する。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/memo に PATCH して調査メモを編集
 */
export function useSetOpsRequestMemo(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; memo: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, memo }): Promise<OpsRequest> => opsApi.setMemo(id, memo),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼の工数ポイント（0–21 の整数、0=対応不要、null で見積もり待ち）を設定する mutation
 * （PATCH /ops/requests/{id}/point）。Firestore 発番の依頼のみ対象（legacy は 404、範囲外は 400）。成功時に
 * 該当依頼の詳細キャッシュを差し替えつつ直前の notes タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の工数ポイント（0–21 / 見積もり待ち。0=対応不要）を設定する。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/point に PATCH して工数ポイントを設定
 */
export function useSetOpsRequestPoint(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; point: number | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, point }): Promise<OpsRequest> => opsApi.setPoint(id, point),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼の作業時間（分 / null=未記録）を設定する mutation
 * （PATCH /ops/requests/{id}/work-minutes）。Firestore 発番の依頼のみ対象（legacy は 404）。
 *
 * 成功時は詳細キャッシュを差し替える（notes タイムラインは保持）。一覧は作業時間の列を持たないので
 * invalidate しない（叩いても表示は変わらず、BQ を引く再取得が走るだけ）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の作業時間（分。null=未記録）を設定する。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧は作業時間を表示しないため再取得しない
 * @graph-connects opsApi [calls] 作業時間設定 API（PATCH /ops/requests/{id}/work-minutes）
 */
export function useSetOpsRequestWorkMinutes(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; workMinutes: number | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, workMinutes }): Promise<OpsRequest> =>
      opsApi.setWorkMinutes(id, workMinutes),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
    },
  });
}

/**
 * 単一 ops 依頼の依頼内容（背景 / 対応依頼 / WFID / CS チケット URL / 種別 / サービス / 対象ユーザー ID）を部分更新する mutation
 * （PATCH /ops/requests/{id}/content）。指定フィールドのみ更新し、wfid / csTicketUrl は null / 空文字で消去する。
 * Firestore 発番の依頼のみ対象（legacy は 404）。成功時に該当依頼の詳細キャッシュを差し替えつつ直前の notes
 * タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の「依頼内容」カードから背景 / 対応依頼 / WFID / CS チケット URL / 種別 / サービス / 対象ユーザー ID を編集する。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する。詳細は常に再取得し（本文編集も活動ログへ積まれるため）、件数バッジ・SLO は種別 / サービスを変えたときだけ再取得する（種別は SLA 目標、サービスは件数内訳を動かすため。本文だけの編集では BQ を無駄に叩かない）
 * @graph-connects opsApi [calls] /ops/requests/{id}/content に PATCH して依頼内容を編集
 */
export function useEditOpsRequestContent(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; edit: OpsContentEdit }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, edit }): Promise<OpsRequest> => opsApi.editContent(id, edit),
    onSuccess: (updated, { id, edit }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
      // 分類を変えたときだけ件数 / SLO を取り直す（サービス別内訳と SLA 目標が動くため）。本文だけの
      // 編集では動かず、どちらも BigQuery を叩くので無条件には invalidate しない（カード側が変更時のみ
      // 送るのでキーの有無で判定できる）。詳細の取り直しは setDetailPreservingNotes が常に行う。
      if (edit.category !== undefined || edit.service !== undefined) {
        void queryClient.invalidateQueries({ queryKey: ['ops', 'counts'] });
        void queryClient.invalidateQueries({ queryKey: ['ops', 'slo'] });
      }
    },
  });
}

/**
 * 単一 ops 依頼へ関連チケットを 1 件紐付ける mutation（POST /ops/requests/{id}/related-tickets）。
 * (target, ref) が同じ既存要素は置き換える（重複排除つき upsert）。linkedBy / linkedAt はサーバーが付与する。
 * Firestore 発番の依頼のみ対象（legacy は 404）。成功時に該当依頼の詳細キャッシュを差し替えつつ直前の notes
 * タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の事前スキャンパネルから参考バグ / 参考 ops を、関連チケットカードのバグチケット紐付け欄から本件調査結果バグ（Backlog MCP 等で別途起票したバグ）を紐付ける。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/related-tickets に POST して関連チケットを紐付け
 */
export function useAddOpsRelatedTicket(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; input: OpsRelatedTicketInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }): Promise<OpsRequest> => opsApi.addRelatedTicket(id, input),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼から (target, ref) の関連チケットを外す mutation（DELETE /ops/requests/{id}/related-tickets）。
 * 参照キーはクエリ（target + ref）で送る。Firestore 発番の依頼のみ対象（legacy は 404）。成功時に該当依頼の
 * 詳細キャッシュを差し替えつつ直前の notes タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の関連チケットカードから人手で紐付けたチケットを解除する。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/related-tickets に DELETE して関連チケットを解除
 */
export function useRemoveOpsRelatedTicket(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; target: 'ops' | 'backlog'; ref: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, target, ref }): Promise<OpsRequest> =>
      opsApi.removeRelatedTicket(id, { target, ref }),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼へ添付ファイルを 1 件アップロードする mutation（POST /ops/requests/{id}/attachments、multipart）。
 * uploadedBy はサーバーが認証セッションから付与する（クライアントは file だけ送る）。成功時に該当依頼の詳細
 * キャッシュを差し替えつつ直前の notes タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の添付カード / 起票フォームから証跡ファイルを追加する。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/attachments に POST（multipart）して添付を追加
 */
export function useUploadOpsAttachment(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; file: File }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }): Promise<OpsRequest> => opsApi.uploadAttachment(id, file),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/**
 * 単一 ops 依頼から添付ファイルを 1 件削除する mutation（DELETE /ops/requests/{id}/attachments/{attachmentId}）。
 * サーバー側でアップロード者 or admin のみ許可（それ以外は 403）。成功時に該当依頼の詳細キャッシュを差し替えつつ
 * 直前の notes タイムラインを保持し、一覧ページを invalidate する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の添付カードからアップロード済み証跡ファイルを削除する（アップロード者 / admin のみ）。成功で詳細キャッシュを差し替え（notes タイムラインは保持）、一覧を再取得する
 * @graph-connects opsApi [calls] /ops/requests/{id}/attachments/{attachmentId} に DELETE して添付を削除
 */
export function useDeleteOpsAttachment(): UseMutationResult<
  OpsRequest,
  Error,
  { id: string; attachmentId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, attachmentId }): Promise<OpsRequest> =>
      opsApi.deleteAttachment(id, attachmentId),
    onSuccess: (updated, { id }) => {
      setDetailPreservingNotes(queryClient, id, updated);
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', 'page'] });
    },
  });
}

/** 翻訳先の言語（ja / en / vi）。 @graph-connects none */
export type OpsTranslateTarget = 'ja' | 'en' | 'vi';

/** 翻訳済みの依頼内容（背景 + 対応依頼 + 調査メモ + notes タイムライン）。 @graph-connects none */
export interface OpsTranslatedContent {
  /** 翻訳済みの背景（5W）。 */
  background: string;
  /** 翻訳済みの対応してほしい内容。 */
  requested: string;
  /** 翻訳済みの調査メモ（未入力なら空文字）。 */
  memo: string;
  /** 翻訳済みの notes タイムライン本文（request.notes と同じ順 = createdAt 昇順）。 */
  notes: string[];
}

/**
 * 単一 ops 依頼の依頼内容（背景 + 対応依頼）を翻訳する mutation（POST /ops/requests/{id}/translate）。
 * 原文は書き換えない read-only 操作なのでキャッシュ invalidation は不要（表示のみ差し替える）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の依頼内容を ja / en / vi へ翻訳する。海外メンバーが依頼を母語で読めるようにする。原文レコードは変えない
 * @graph-connects opsApi [calls] /ops/requests/{id}/translate に POST して依頼内容を翻訳
 */
export function useTranslateOpsRequest(): UseMutationResult<
  OpsTranslatedContent,
  Error,
  { id: string; target: OpsTranslateTarget }
> {
  return useMutation({
    mutationFn: ({ id, target }): Promise<OpsTranslatedContent> =>
      opsApi.translateRequest(id, target),
  });
}

/** 依頼者ビュー等で使う全件取得（My-Requests スライスで実 API 化予定。現状モック）。 @graph-connects none */
export function useOpsRequests(): UseQueryResult<OpsRequest[]> {
  return useQuery({
    queryKey: ['ops', 'requests', 'all'],
    queryFn: (): Promise<OpsRequest[]> => Promise.resolve(OPS_REQUESTS),
  });
}

/** 全サービスの担当割当を取得する（GET /ops/assignments）。 @graph-connects none */
export function useOpsAssignments(): UseQueryResult<OpsAssignment[]> {
  return useQuery({
    queryKey: ['ops', 'assignments'],
    queryFn: (): Promise<OpsAssignment[]> => opsApi.listAssignments(),
  });
}

/**
 * 1 サービスの担当割当を更新する mutation（PUT /ops/assignments/{service}）。成功で割当一覧を再取得する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当を更新する（管理ビュー）。成功時に割当一覧クエリを invalidate して最新の割当を反映する
 * @graph-connects opsApi [calls] /ops/assignments/{service} に PUT して割当を更新
 */
export function useSetOpsAssignment(): UseMutationResult<
  OpsAssignment,
  Error,
  { service: OpsService; edit: OpsAssignmentEdit }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ service, edit }): Promise<OpsAssignment> => opsApi.setAssignment(service, edit),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ops', 'assignments'] });
    },
  });
}

/**
 * 記録済みロスター変更履歴（実績。新しい順）を取得する（GET /ops/assignments/history）。
 * projection ではなく実際に記録された変更のみ（記録が無ければ空 = History には何も出さない）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 担当割当の記録済み変更履歴（実績）を取得する。割当ボードの History タブ用
 * @graph-connects opsApi [calls] /ops/assignments/history から記録済みロスター変更を取得
 */
export function useOpsAssignmentHistory(): UseQueryResult<OpsAssignmentEvent[]> {
  return useQuery({
    queryKey: ['ops', 'assignments', 'history'],
    queryFn: (): Promise<OpsAssignmentEvent[]> => opsApi.listAssignmentHistory(),
  });
}

/**
 * ops 依頼からバグチケット下書きを AI 生成する mutation（POST /ops/requests/{id}/bug-ticket/draft）。
 * Backlog には書き込まない（起票前レビュー用）。詳細ビューの起票モーダルを開いたときに実行する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の起票モーダルで、依頼内容から esa #4070 標準に沿ったバグチケット下書きを AI 生成する（Backlog 非書込）
 * @graph-connects opsApi [calls] /ops/requests/{id}/bug-ticket/draft に POST してバグチケット下書きを取得
 */
export function useDraftOpsBugTicket(): UseMutationResult<
  OpsBugTicketDraft,
  Error,
  { id: string; similarBugKeys: string[] }
> {
  return useMutation({
    mutationFn: ({ id, similarBugKeys }): Promise<OpsBugTicketDraft> =>
      opsApi.draftBugTicket(id, similarBugKeys),
  });
}

/**
 * 人手確認後の下書きで Backlog にバグチケットを起票する mutation（POST /ops/requests/{id}/bug-ticket）。
 * 成功で依頼に backlogTicketUrl が紐付くため、該当依頼の詳細キャッシュを invalidate して再取得する。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細の起票モーダルの Proceed で Backlog にバグチケットを起票する。成功で依頼詳細を再取得して起票済み URL を反映する
 * @graph-connects opsApi [calls] /ops/requests/{id}/bug-ticket に POST してバグチケットを起票
 */
export function useCreateOpsBugTicket(): UseMutationResult<
  CreatedOpsBugTicket,
  Error,
  { id: string; input: CreateOpsBugTicketInput }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }): Promise<CreatedOpsBugTicket> => opsApi.createBugTicket(id, input),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['ops', 'requests', id] });
    },
  });
}
